import React, { JSX, memo, useCallback, useMemo } from 'react';

import { Platform, Pressable, View } from 'react-native';

import { useTranslation } from 'react-i18next';

import { isWeb } from 'utils/responsiveness';

import { EPGChannel, EPGConfig } from '../../types';
import getStyles from './styles';

type Props = {
    config: EPGConfig;
    item: EPGChannel<any>;
    renderContent: (channel: EPGChannel<any>) => JSX.Element;
    onCellPress?: (channel: EPGChannel<any>) => void;
    onCellAccessibilityAction?: (channel: EPGChannel<any>) => void;
};

const ChannelCellRenderer = ({
    config,
    item,
    renderContent,
    onCellPress,
    onCellAccessibilityAction,
}: Props) => {
    const { t } = useTranslation();
    const styles = useMemo(() => getStyles(config), []);

    const onPress = useCallback(() => {
        onCellPress?.(item);
    }, [onCellPress, item]);

    const onAccessibilityAction = useCallback(
        (event) => {
            onCellPress?.(item);

            if (event.nativeEvent.actionName === 'activate') {
                onCellAccessibilityAction?.(item);
            }
        },
        [onCellAccessibilityAction, onCellPress, item],
    );

    return Platform.isTV ? (
        <View style={styles.container}>{renderContent(item)}</View>
    ) : (
        <View
            accessible
            accessibilityLabel={item.name}
            accessibilityRole="button"
            accessibilityActions={[{ name: 'activate' }]}
            onAccessibilityAction={onAccessibilityAction}
            {...(Platform.OS === 'ios' && {
                accessibilityHint: t('ACCESSIBILITY_GENERAL_TAPP_TWICE'),
            })}
        >
            <Pressable
                style={styles.container}
                onPress={onPress}
                importantForAccessibility="no-hide-descendants"
                accessibilityElementsHidden
                role="button"
                {...(isWeb && { accessibilityLabel: item.name })}
            >
                {renderContent(item)}
            </Pressable>
        </View>
    );
};

export default memo(
    ChannelCellRenderer,
    (prevProps: Props, nextProps: Props) =>
        prevProps.item.id === nextProps.item.id &&
        prevProps.renderContent === nextProps.renderContent &&
        prevProps.onCellPress === nextProps.onCellPress,
);
